import { ISectionLogger } from "@multiweb/logging";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { PasswordEdit } from "../Controls/PasswordEdit";
import { MessageWindow } from "../MessageWindow";
import { IContentBuilder } from "./IContentBuilder";

/**
 * Builder, který sestaví dialog vložení hesla pro (de)šifrování objektu.
 * @internal
 */
export class PasswordDialogBuilder implements IContentBuilder {
    private _element: HTMLElement;
    private _input: HTMLElement;
    private _inputConfirm: HTMLElement;
    private _passwordEdit: PasswordEdit;
    private _passwordConfirmEdit: PasswordEdit;
    private _inputCaption: HTMLLabelElement;
    private _inputConfirmCaption: HTMLLabelElement;
    private _parent: MessageWindow;
    private _textLocKey: string;
    private _object: OWObject;
    private _passwordDialogType: PasswordDialogBuilderType;
    private _okAction: (password: string) => Promise<boolean>;
    private _closeAction: () => Promise<boolean>;
    private readonly _logger: ISectionLogger;

    private get _passwordValue(): string {
        return this._passwordEdit ? this._passwordEdit.controlValue : "";
    }

    /**
     * Vytvoří dialog pro zadání hesla pro šifrování/dešifrování záznamu.
     * @param parent Instance MessageWindow.
     * @param textLocKey Lokalizační klíč, který bude zobrazen jako zpráva dialogu.
     * @param obj Refence objektu, který se má (de)šifrovat.
     * @param passwordDialogType Určuje k jakému účelu má být builder určen - k nastavení nebo ověření hesla
     */
    constructor(parent: MessageWindow, textLocKey: string, obj: OWObject, passwordDialogType: PasswordDialogBuilderType, okAction: (password: string) => Promise<boolean>,
        closeAction: () => Promise<boolean>, logger: ISectionLogger) {
        this._textLocKey = textLocKey;
        this._parent = parent;
        this._object = obj;
        this._passwordDialogType = passwordDialogType;
        this._logger = logger;
        if (okAction)
            this._okAction = async (password: string): Promise<boolean> => {
                return await okAction(password);
            };
        if (closeAction)
            this._closeAction = async (): Promise<boolean> => {
                return await closeAction();
            };
    }

    private async _build(): Promise<void> {
        if (!this._element) {
            const passwordLockey = "GUI-462928"; //Heslo
            const passwordConfirmLockey = "GUI-735909"; //Potvrzení hesla
            const passwordConfirmErrorLockey = "GUI-735910"; //Hesla se neshodují

            const content = createElement();
            this._input = createElement({ attrs: { "data-ispassword": "true" } });
            this._passwordEdit = PasswordEdit.createWithEmptyRights(this._input);

            const text = StringUtils.format(await Localization.getTextPromise(this._textLocKey), [this._object.objectIdentity]);
            const textElement = createElement({ value: text, classes: "message" });
            this._inputCaption = createElement({ type: "label", classes: "Caption" }) as HTMLLabelElement;

            Localization.addLocData(this._inputCaption, passwordLockey);

            const onPasswordInput = (passwordEdit: PasswordEdit, captionElement: HTMLElement, lockey: string) =>
                () => {
                    passwordEdit.setValid();
                    this._toggleOkDisabled();
                    captionElement.classList.remove("invalid");
                    Localization.addLocData(captionElement, lockey);
                };

            const onKeyPress = (passwordEdit: PasswordEdit, captionElement: HTMLElement, lockey: string) =>
                (event: KeyboardEvent) => {
                    if (event.key === "Enter") //při stisknutí enter zavolá stisk OK na dialogu.
                        this._parent.okButton.click();
                    else
                        onPasswordInput(passwordEdit, captionElement, lockey);
                };

            this._input.addEventListener("input", onPasswordInput(this._passwordEdit, this._inputCaption, passwordLockey));
            this._input.addEventListener("paste", onPasswordInput(this._passwordEdit, this._inputCaption, passwordLockey));

            this._input.addEventListener("keypress", onKeyPress(this._passwordEdit, this._inputCaption, passwordLockey));

            this._parent.okCallback = async (): Promise<boolean> => {
                return await this._okClickedCallback();
            };
            this._parent.otherCallback = async (): Promise<boolean> => {
                return await this._closeClickedCallback();
            };
            this._parent.focusElement = this._passwordEdit;

            content.appendChild(textElement);
            content.appendChild(this._inputCaption);
            content.appendChild(this._input);

            if (this._object.isNew) {
                this._inputConfirm = createElement({ attrs: { "data-ispassword": "true" } });
                this._passwordConfirmEdit = PasswordEdit.createWithEmptyRights(this._inputConfirm);

                this._inputConfirmCaption = createElement({
                    type: "label",
                    classes: ["Caption", "password-confirm"],
                }) as HTMLLabelElement;
                Localization.addLocData(this._inputConfirmCaption, passwordConfirmLockey);

                this._inputConfirm.addEventListener("keypress", onKeyPress(this._passwordConfirmEdit, this._inputConfirmCaption, passwordConfirmLockey));

                this._input.addEventListener("input", onPasswordInput(this._passwordConfirmEdit, this._inputConfirmCaption, passwordConfirmLockey));
                this._inputConfirm.addEventListener("input", onPasswordInput(this._passwordConfirmEdit, this._inputConfirmCaption, passwordConfirmLockey));

                const passSameCheck = () => {
                    if (this._passwordEdit.controlValue !== this._passwordConfirmEdit.controlValue) {
                        this._passwordConfirmEdit.setInvalid(null);
                        this._inputConfirmCaption.classList.add("invalid");
                        Localization.addLocData(this._inputConfirmCaption, passwordConfirmErrorLockey);
                        return false;
                    }
                    return true;
                };

                this._parent.okCallback = async (): Promise<boolean> => {
                    return passSameCheck() && await this._okClickedCallback();
                };

                this._input.addEventListener("change", passSameCheck);
                this._inputConfirm.addEventListener("change", passSameCheck);

                content.appendChild(this._inputConfirmCaption);
                content.appendChild(this._inputConfirm);
            }

            this._toggleOkDisabled();

            this._element = content;
        }
    }

    /**
     * Metoda, která se zavolá při stisknutí OK tlačítka. Pokud bylo zadáno špatné heslo, dialog místo zavření zobrazí chybu.
     */
    private async _okClickedCallback(): Promise<boolean> {
        if (!this._okAction)
            return false;
        switch (this._passwordDialogType) {
            case PasswordDialogBuilderType.enter:
                return await this._okAction(this._passwordValue);
            case PasswordDialogBuilderType.verify: {
                this._passwordEdit.enabledVisual = false;
                const verificationResult = await this._okAction(this._passwordValue);
                this._passwordEdit.enabledVisual = true;
                if (!verificationResult)
                    this._setInvalid();
                return verificationResult;
            }
            default:
                this._logger.error("Invalid passwordDialogType.");
                return false;
        }
    }

    /**
     * Metoda, která se zavolá při stisknutí tlačítka Zavřít.
     */
    private async _closeClickedCallback(): Promise<boolean> {
        if (!this._closeAction)
            return true;
        return await this._closeAction();
    }

    /**
     * Pokud je heslo prázdné, přepne OK tlačítko do neaktivního stavu.
     */
    private _toggleOkDisabled(): void {
        const passEmpty = !this._passwordEdit.controlValue || this._passwordEdit.controlValue.length === 0;
        const passConfirmEmpty = this._object.isNew && (!this._passwordConfirmEdit.controlValue || this._passwordConfirmEdit.controlValue.length === 0);

        if (passEmpty || passConfirmEmpty) {
            this._parent.okButton.setAttribute("disabled", "disabled");
            this._parent.okButton.classList.add("Disabled");
        } else {
            this._parent.okButton.removeAttribute("disabled");
            this._parent.okButton.classList.remove("Disabled");
        }
    }

    /**
     * Nastaví stav pří naplatném heslu
     */
    private _setInvalid(): void {
        this._passwordEdit.setInvalid(null);
        this._inputCaption.classList.add("invalid");
        Localization.addLocData(this._inputCaption, "GUI-596950"); //Chybné heslo
    }

    public async build(): Promise<HTMLElement> {
        await this._build();
        return this._element;
    }
}

/**
 * Určuje k jakému účelu má být builder určen - k nastavení nebo ověření hesla
 */
export enum PasswordDialogBuilderType {
    enter, // nastavení hesla
    verify, // ověření hesla
}
